import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import { ProductRelation } from "../../types/types";
import RelatedProductCard from "./RelatedProductCard";
import { PRODUCT_DETAIL_TEXTS } from "../../pages/shared/constants";
import { dedupeRelations } from "../../utils/relations";
import { StyledDivider } from "./styledComponents";

const CardsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),
}));

interface RelatedProductsProps {
  relations: ProductRelation[];
}

/**
 * Decision (EB-520): the admin preview hides inactive relations too.
 *
 * `Item` / `ItemMobile` render here on both `ProductDetailPage` (the shop) and
 * `ItemMaintenancePage` (the admin preview), so this component cannot tell the two apart without
 * being told — and it deliberately isn't.
 *
 * A preview that shows more than the customer sees is not a preview. The admin loses nothing by
 * it: `RelatedProductsEditor`, further down that same page, lists **every** relation including the
 * inactive ones, sourced from `getItems`, which returns inactive rows. So the full picture stays
 * one screen away while the preview keeps its one job — showing what a shopper gets.
 */

const RelatedProducts: React.FC<RelatedProductsProps> = ({ relations }) => {
  // Defensive (EB-522). Curated rows carried repeated ids, which rendered the same card twice and
  // — since the list keys on `relation.id` — handed React duplicate keys. The stored data was
  // cleaned up, but the render path should not rely on that holding.
  const uniqueRelations = useMemo(() => dedupeRelations(relations), [relations]);

  // Which targets turned out not to be shoppable (EB-520). Only the cards can tell us — each one
  // fetches its own product — so they report back and this decides whether a section is left.
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(() => new Set());

  const handleResolved = useCallback((id: string, visible: boolean) => {
    setHiddenIds((prev) => {
      // Bail out when nothing changed, or every card's report would re-render the whole section.
      if (visible !== prev.has(id)) return prev;

      const next = new Set(prev);
      if (visible) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleCount = uniqueRelations.filter((r) => !hiddenIds.has(r.id)).length;

  // No heading over an empty row. `visibleCount` starts at the full count and only falls as cards
  // resolve, so the section shows while loading and disappears if nothing survives — rather than
  // flickering into existence once the first card comes back.
  if (visibleCount === 0) return null;

  return (
    <Box>
      {/* The separator lives inside this return, not in the parent (EB-539). The section is a
          full-width band now, so its rule spans the whole container — and a band that resolves to
          nothing has to take that rule with it, or an orphaned line is left across the page. Only
          this component knows whether anything survived. The heading and cards keep the page's
          horizontal padding; the rule deliberately does not. */}
      <StyledDivider />
      <Box px={2} pt={1} pb={2}>
        <Typography variant="h6" fontWeight="bold">
          {PRODUCT_DETAIL_TEXTS.relatedProductsTitle}
        </Typography>
        <CardsContainer>
          {uniqueRelations.map((relation) => (
            <RelatedProductCard key={relation.id} relation={relation} onResolved={handleResolved} />
          ))}
        </CardsContainer>
      </Box>
    </Box>
  );
};

export default RelatedProducts;
